import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Image,
  useColorModeValue,
  Heading,
} from '@chakra-ui/react';
import { FaEuroSign } from 'react-icons/fa';

// Helper per ottenere il colore del badge stato
const getStatoColor = (stato) => {
  const statoColors = {
    disponibile: 'green',
    prenotato: 'yellow',
    venduto: 'gray',
    terminato: 'gray',
  };
  return statoColors[stato] || 'gray';
};

// Helper per ottenere il testo del badge stato
const getStatoLabel = (stato) => {
  const statoLabels = {
    disponibile: 'Disponibile',
    prenotato: 'Prenotato',
    venduto: 'Venduto',
    terminato: 'Terminato',
  };
  return statoLabels[stato] || stato;
};

// Helper per ottenere l'immagine principale
const getMainImage = (bazar) => {
  try {
    // Prova diverse strutture possibili - controlla PRIMA bazar.galleria direttamente se è un array
    let galleria = [];
    if (Array.isArray(bazar.galleria) && bazar.galleria.length > 0) {
      galleria = bazar.galleria;
    } else if (
      Array.isArray(bazar.attributes?.galleria) &&
      bazar.attributes.galleria.length > 0
    ) {
      galleria = bazar.attributes.galleria;
    } else if (
      bazar.attributes?.galleria?.data &&
      Array.isArray(bazar.attributes.galleria.data)
    ) {
      galleria = bazar.attributes.galleria.data;
    } else if (bazar.galleria?.data && Array.isArray(bazar.galleria.data)) {
      galleria = bazar.galleria.data;
    }

    if (galleria.length > 0) {
      const firstImage = galleria[0];

      // Gestisce diverse strutture di Strapi (v4 e v5)
      let imageData = firstImage;
      if (firstImage.attributes) {
        imageData = firstImage.attributes;
      } else if (firstImage.data && firstImage.data.attributes) {
        imageData = firstImage.data.attributes;
      }

      // Prova diversi percorsi per l'URL
      const imageUrl =
        imageData.url ||
        imageData.formats?.large?.url ||
        imageData.formats?.medium?.url ||
        imageData.formats?.small?.url ||
        imageData.formats?.thumbnail?.url;

      if (imageUrl) {
        // Se l'URL inizia già con http/https, restituiscilo così com'è
        if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
          return imageUrl;
        }

        // Se l'URL inizia con /uploads, potrebbe funzionare come relativo (tramite proxy in develop)
        // In produzione, se Strapi è su dominio diverso, serve l'URL completo
        const baseUrl =
          process.env.REACT_APP_STRAPI_API_URL ||
          process.env.REACT_APP_STRAPI_URL;

        // Se abbiamo un baseUrl configurato, usalo
        if (baseUrl) {
          const cleanBaseUrl = baseUrl.replace(/\/$/, '');
          const cleanImageUrl = imageUrl.startsWith('/')
            ? imageUrl
            : `/${imageUrl}`;
          const finalUrl = `${cleanBaseUrl}${cleanImageUrl}`;

          return finalUrl;
        }

        // Se non c'è baseUrl, prova come URL relativo (funziona se Strapi è sullo stesso dominio o tramite proxy)
        const cleanImageUrl = imageUrl.startsWith('/')
          ? imageUrl
          : `/${imageUrl}`;

        return cleanImageUrl;
      }
    }
  } catch (error) {
    console.error('Errore nel recupero immagine principale:', error);
  }
  return null;
};

// Helper per ottenere il nome del socio venditore
const getVenditoreName = (bazar) => {
  // Prova diverse strutture possibili per il socio
  // Strapi v5 può restituire il socio in diverse strutture:
  // - attributes.socio.data (relazione popolata)
  // - attributes.socio (relazione popolata direttamente)
  // - socio.data o socio (se non c'è attributes)
  let socio = null;

  if (bazar.attributes?.socio?.data) {
    socio = bazar.attributes.socio.data;
  } else if (bazar.attributes?.socio) {
    socio = bazar.attributes.socio;
  } else if (bazar.socio?.data) {
    socio = bazar.socio.data;
  } else if (bazar.socio) {
    socio = bazar.socio;
  }

  if (socio) {
    // Il socio può avere i campi direttamente o dentro attributes
    const nomeSocio = socio.nomeSocio || socio.attributes?.nomeSocio || '';
    const cognomeSocio =
      socio.cognomeSocio || socio.attributes?.cognomeSocio || '';
    const nomeCompleto = `${nomeSocio} ${cognomeSocio}`.trim();

    return nomeCompleto || 'Socio CIL';
  }
  return 'Socio CIL';
};

export default function BazarCard({ bazar, index }) {
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardShadow = useColorModeValue('lg', 'xl');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const bazarData = bazar.attributes || bazar;
  const documentId = bazar.documentId || bazar.id;
  const stato = bazarData.stato || 'disponibile';
  const titolo = bazarData.titolo || 'Senza titolo';
  const { prezzo, categoria, condizione } = bazarData;
  const prezzoNegoziabile = bazarData.prezzoNegoziabile || false;

  const mainImage = getMainImage(bazar);
  const venditoreName = getVenditoreName(bazar);

  // Debug: log per vedere cosa arriva
  console.log('=== DEBUG BAZAR CARD ===');
  console.log('Bazar:', bazar);
  console.log('Bazar attributes:', bazar.attributes);
  console.log('Bazar attributes.socio:', bazar.attributes?.socio);
  console.log('VenditoreName:', venditoreName);

  const handleCardClick = () => {
    navigate(`/bazar/${documentId}`);
  };

  return (
    <Box
      bg={cardBg}
      borderRadius="lg"
      boxShadow={cardShadow}
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        transform: 'translateY(-4px)',
        boxShadow: 'xl',
      }}
      onClick={handleCardClick}
      data-testid={
        typeof index === 'number' ? `bazar-card-${index}` : undefined
      }
    >
      {/* Immagine principale */}
      <Box
        position="relative"
        width="100%"
        height="200px"
        bg={useColorModeValue('gray.100', 'gray.700')}
        overflow="hidden"
      >
        {mainImage ? (
          <Image
            src={mainImage}
            alt={titolo}
            width="100%"
            height="100%"
            objectFit="cover"
          />
        ) : (
          <Box
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color={useColorModeValue('gray.400', 'gray.500')}
          >
            <Text fontSize="sm">Nessuna immagine</Text>
          </Box>
        )}

        {/* Badge stato in alto a destra */}
        <Badge
          position="absolute"
          top={2}
          right={2}
          colorScheme={getStatoColor(stato)}
          fontSize="xs"
          px={2}
          py={1}
          borderRadius="md"
        >
          {getStatoLabel(stato)}
        </Badge>
      </Box>

      {/* Contenuto card */}
      <VStack align="stretch" spacing={3} p={4}>
        {/* Titolo */}
        <Heading size="sm" noOfLines={2}>
          {titolo}
        </Heading>

        {/* Prezzo e badge negoziabile */}
        <HStack justify="space-between" align="center">
          {prezzo !== null && prezzo !== undefined ? (
            parseFloat(prezzo) === 0 ? (
              <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                Regalo!
              </Badge>
            ) : (
              <HStack spacing={1}>
                <FaEuroSign size="14px" />
                <Text fontWeight="bold" fontSize="lg">
                  {parseFloat(prezzo).toFixed(2)}
                </Text>
              </HStack>
            )
          ) : (
            <Text fontSize="sm" color="gray.500">
              Prezzo non specificato
            </Text>
          )}

          {prezzoNegoziabile && parseFloat(prezzo) !== 0 && (
            <Badge colorScheme="orange" fontSize="xs">
              Negoziabile
            </Badge>
          )}
        </HStack>

        {/* Categoria e condizione */}
        <HStack spacing={2} flexWrap="wrap">
          {categoria && (
            <Badge colorScheme="blue" fontSize="xs">
              {categoria}
            </Badge>
          )}
          {condizione && (
            <Badge colorScheme="purple" fontSize="xs">
              {condizione}
            </Badge>
          )}
        </HStack>

        {/* Venditore */}
        <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.400')}>
          Venditore:{' '}
          <Text as="span" fontWeight="medium">
            {venditoreName}
          </Text>
        </Text>

        {/* CTA */}
        <Button
          colorScheme="yellow"
          size="sm"
          width="100%"
          onClick={(e) => {
            e.stopPropagation();
            handleCardClick();
          }}
        >
          Vedi dettagli
        </Button>
      </VStack>
    </Box>
  );
}
